// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect, useCallback } from 'react'
import { InfoBlock }     from '@/components/ui/InfoBlock'
import { AddressFields } from '@/components/ui/AddressFields'
import { Input }         from '@/components/ui/Input'
import { secondHomeExamples } from '@/lib/constants/secondHomeExamples'
import { Card }          from '@/components/ui/Card'
import { useWizardStore, useAuthStore } from '@/store'
import { useAutosave }   from '@/hooks/useAutosave'
import { loadSecondHome, saveSecondHome, EMPTY_SECOND_HOME, type SecondHomeData } from '@/services/wizard'
import type { SaveStatus } from '@/hooks/useAutosave'

const TYPES = [
  { value: 'vacation',   label: 'Vakantiewoning' },
  { value: 'recreation', label: 'Recreatiewoning' },
  { value: 'apartment',  label: 'Appartement' },
  { value: 'other',      label: 'Anders' },
]

// V41: landen-dropdown - Nederland toont BAG-autofill, buitenland vrije invoer
const LANDEN = [
  { value: 'NL',    label: 'Nederland' },
  { value: 'BE',    label: 'België' },
  { value: 'DE',    label: 'Duitsland' },
  { value: 'FR',    label: 'Frankrijk' },
  { value: 'ES',    label: 'Spanje' },
  { value: 'IT',    label: 'Italië' },
  { value: 'other', label: 'Anders' },
]

// Normaliseert bestaande vrije-tekst land-waarden naar dropdown-codes
// Bestaande gebruikers die "Nederland" of "Italië" hadden ingevuld krijgen correct geselecteerde optie
function normalizeLand(raw: string): string {
  if (!raw) return ''
  const l = raw.toLowerCase().trim()
  if (l.includes('neder') || l === 'nl') return 'NL'
  if (l.includes('belg')  || l === 'be') return 'BE'
  if (l.includes('duits') || l === 'de' || l.includes('german')) return 'DE'
  if (l.includes('frank') || l === 'fr' || l.includes('france')) return 'FR'
  if (l.includes('span')  || l === 'es' || l.includes('spain'))  return 'ES'
  if (l.includes('ital')  || l === 'it' || l.includes('italy'))  return 'IT'
  if (l.length > 0) return 'other'
  return ''
}

export function NewStep05SecondHome({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { answers, setAnswer } = useWizardStore()
  const { userId }             = useAuthStore()
  const [data,  setData]       = useState<SecondHomeData>(EMPTY_SECOND_HOME)
  const [ready, setReady]      = useState(false)

  const hasSecondHome = answers.hasSecondHome

  useEffect(() => {
    if (!userId) return
    loadSecondHome(userId).then(d => {
      if (d) {
        // V41: normaliseer bestaande vrije-tekst land naar dropdown-code
        const normalized = { ...EMPTY_SECOND_HOME, ...d }
        if (d.country && !['NL','BE','DE','FR','ES','IT','other'].includes(d.country)) {
          normalized.country = normalizeLand(d.country)
        }
        setData(normalized)
      }
      setReady(true)
    })
    onSaveStatus('idle')
  }, [userId, onSaveStatus])

  const save = useCallback(async (d: SecondHomeData) => {
    if (userId) await saveSecondHome(userId, d)
  }, [userId])
  const { status } = useAutosave({ data, onSave: save, enabled: ready && hasSecondHome === true })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function setHas(v: string) {
    setAnswer('hasSecondHome', v === 'yes')
    setData(p => ({ ...p, hasSecondHome: v }))
  }
  function upd<K extends keyof SecondHomeData>(k: K, v: SecondHomeData[K]) {
    setData(p => ({ ...p, [k]: v }))
  }
  function updContact(field: keyof SecondHomeData['localContact'], value: string) {
    setData(p => ({ ...p, localContact: { ...p.localContact, [field]: value } }))
  }
  function updGp(field: keyof SecondHomeData['localGp'], value: string) {
    setData(p => ({ ...p, localGp: { ...p.localGp, [field]: value } }))
  }
  function updDocs(field: keyof SecondHomeData['documents'], value: string) {
    setData(p => ({ ...p, documents: { ...p.documents, [field]: value } }))
  }

  // V54: landafhankelijke voorbeelden/placeholders
  const ex = secondHomeExamples(data.country)

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Tweede woning</h2>
        <p className="text-sm text-ink-3">Een vakantiewoning, recreatiewoning of ander tweede verblijf.</p>
      </div>

      {/* Goed om te weten VOOR de vraag (punt 21) */}
      <InfoBlock title="Goed om te weten - tweede woning" variant="blue" defaultOpen={false}>
        <p>Een huis op afstand, zeker in het buitenland, brengt extra zaken mee. Leg vast wie uw naasten kunnen bereiken als u er langere tijd verblijft of als er iets aan de hand is.</p>
        <p>Zorg voor een <strong>lokaal aanspreekpunt</strong> dat ter plaatse kan helpen: een beheerder, een buurman of de makelaar van destijds. Bij langdurig verblijf in het buitenland is ook een <strong>lokale huisarts</strong> belangrijk.</p>
        <p>Noteer waar de eigendomsakte, de sleutels en eventuele alarmcodes liggen. In de praktijk kost een buitenlandse woning soms maanden, simpelweg omdat niemand de juiste papieren of contactpersoon kon vinden.</p>
                              </InfoBlock>

      {/* Ja / Nee */}
      <div className="flex flex-col gap-2">
        {[{ value: 'yes', label: 'Ja' }, { value: 'no', label: 'Nee' }].map(opt => (
          <button key={opt.value} type="button" onClick={() => setHas(opt.value)}
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-all ${
              (hasSecondHome === true && opt.value === 'yes') || (hasSecondHome === false && opt.value === 'no')
                ? 'border-sage-600 bg-sage-50' : 'border-sand-300 bg-white hover:border-sage-300'
            }`}>
            <div className={`h-5 w-5 rounded-full border-2 flex-shrink-0 ${
              (hasSecondHome === true && opt.value === 'yes') || (hasSecondHome === false && opt.value === 'no')
                ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
            }`} />
            <span className="text-base font-semibold text-ink-2">{opt.label}</span>
          </button>
        ))}
      </div>

      {hasSecondHome === true && (
        <div className="flex flex-col gap-4 animate-slide-down">

          {/* Type */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Type woning</h3>
            <div className="flex flex-col gap-2">
              {TYPES.map(t => (
                <button key={t.value} type="button" onClick={() => upd('type', t.value)}
                  className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                    data.type === t.value ? 'border-sage-600 bg-sage-50' : 'border-sand-300 hover:border-sage-300'
                  }`}>
                  <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${data.type === t.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'}`} />
                  <span className="text-sm text-ink">{t.label}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Adres - V41: land eerst, conditionele velden */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Adres</h3>
            <div className="flex flex-col gap-3">

              {/* Stap 1: Land kiezen */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-ink-2">
                  Land <span className="text-sage-600">*</span>
                </label>
                <div className="flex flex-col gap-2">
                  {LANDEN.map(l => (
                    <button key={l.value} type="button"
                      onClick={() => {
                        upd('country', l.value)
                        // Reset adresvelden bij wisselen van land
                        if (l.value !== 'NL') {
                          upd('postalCode', '')
                          upd('houseNumber', '')
                        }
                      }}
                      className={`flex items-center gap-3 rounded-xl border-2 px-4 py-2.5 text-left transition-all ${
                        data.country === l.value
                          ? 'border-sage-600 bg-sage-50'
                          : 'border-sand-300 bg-white hover:border-sage-300'
                      }`}
                    >
                      <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${
                        data.country === l.value ? 'border-sage-600 bg-sage-600' : 'border-sand-300'
                      }`} />
                      <span className="text-sm text-ink">{l.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Stap 2: Adresvelden - conditioneel op land */}
              {data.country === 'NL' && (
                <AddressFields
                  values={{
                    postalCode:  data.postalCode,
                    houseNumber: data.houseNumber,
                    street:      data.street,
                    city:        data.city,
                  }}
                  onChange={(field, value) => upd(field as keyof typeof data, value)}
                  showAddition={false}
                  labels={{ street: 'Straat', city: 'Plaats' }}
                />
              )}

              {data.country && data.country !== 'NL' && (
                <div className="flex flex-col gap-3">
                  <Input
                    label="Stad / Plaats"
                    value={data.city}
                    onChange={e => upd('city', e.target.value)}
                    placeholder="Bijv: Rome, Barcelona, Bordeaux"
                  />
                  <Input
                    label="Adres"
                    value={data.street}
                    onChange={e => upd('street', e.target.value)}
                    placeholder="Bijv: Via Roma 12"
                  />
                </div>
              )}

            </div>
          </Card>

          {/* Lokaal aanspreekpunt */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Lokaal aanspreekpunt / beheerder</h3>
            <p className="text-xs text-ink-4 mb-3">Bijv: beheerder, buurman, makelaar, parkbeheer of verhuurorganisatie.</p>
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Naam" value={data.localContact.name} onChange={e => updContact('name', e.target.value)} placeholder="Naam" />
                <Input label="Functie / relatie" value={data.localContact.role} onChange={e => updContact('role', e.target.value)} placeholder="Bijv: beheerder" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Telefoon" type="tel" value={data.localContact.phone} onChange={e => updContact('phone', e.target.value)} placeholder={ex.contactPhone} />
                <Input label="E-mail" type="email" value={data.localContact.email} onChange={e => updContact('email', e.target.value)} placeholder="naam@mail.com" />
              </div>
            </div>
          </Card>

          {/* Lokale huisarts */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Lokale huisarts (optioneel)</h3>
            <p className="text-xs text-ink-4 mb-3">Relevant bij langdurig verblijf in het buitenland.</p>
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Naam huisarts" value={data.localGp.name} onChange={e => updGp('name', e.target.value)} placeholder={ex.gpName} />
                <Input label="Praktijk" value={data.localGp.practice} onChange={e => updGp('practice', e.target.value)} placeholder={ex.gpPractice} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Telefoon" type="tel" value={data.localGp.phone} onChange={e => updGp('phone', e.target.value)} placeholder={ex.gpPhone} />
                <Input label="Plaats" value={data.localGp.city} onChange={e => updGp('city', e.target.value)} placeholder={ex.gpCity} />
              </div>
            </div>
          </Card>

          {/* Documentlocaties */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Documentlocaties</h3>
            <div className="flex flex-col gap-3">
              <Input label="Eigendomsakte" value={data.documents.ownership} onChange={e => updDocs('ownership', e.target.value)} placeholder={ex.docOwnership} />
              <Input label="Sleutels" value={data.documents.keys} onChange={e => updDocs('keys', e.target.value)} placeholder={ex.docKeys} />
              <Input label="Alarmcodes" value={data.documents.alarmCodes} onChange={e => updDocs('alarmCodes', e.target.value)} placeholder={ex.docAlarm} />
              <Input label="Verzekeringsdocumenten" value={data.documents.insurance} onChange={e => updDocs('insurance', e.target.value)} placeholder={ex.docInsurance} />
            </div>
          </Card>

          {/* V38: Gestructureerde hypotheekinformatie tweede woning */}
          <Card>
            <h3 className="text-sm font-semibold text-ink mb-3">Hypotheek</h3>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                {[
                  { value: 'yes',     label: 'Ja, er is een hypotheek' },
                  { value: 'no',      label: 'Nee' },
                  { value: 'unknown', label: 'Onbekend' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => upd('hasMortgage', opt.value)}
                    className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm flex-1 justify-center transition-all ${
                      data.hasMortgage === opt.value ? 'border-sage-600 bg-sage-50 text-sage-800 font-medium' : 'border-sand-300 bg-white text-ink-3 hover:border-sage-300'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {data.hasMortgage === 'yes' && (
                <div className="flex flex-col gap-3 animate-slide-down">
                  {/* Land hypotheekverstrekker */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-ink-2">Land hypotheekverstrekker</label>
                    <select value={data.mortgageCountry}
                      onChange={e => upd('mortgageCountry', e.target.value)}
                      className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors">
                      <option value="">Selecteer land...</option>
                      <option value="NL">Nederland</option>
                      <option value="IT">Italië</option>
                      <option value="ES">Spanje</option>
                      <option value="FR">Frankrijk</option>
                      <option value="DE">Duitsland</option>
                      <option value="BE">België</option>
                      <option value="anders">Ander land</option>
                    </select>
                  </div>
                  {/* NL: dropdown */}
                  {data.mortgageCountry === 'NL' && (
                    <div className="flex flex-col gap-1">
                      <label className="text-sm font-medium text-ink-2">Hypotheekverstrekker</label>
                      <select value={data.mortgageProvider}
                        onChange={e => upd('mortgageProvider', e.target.value)}
                        className="rounded-xl border-2 border-sand-300 bg-white px-4 py-3 text-sm text-ink focus:border-sage-400 focus:outline-none transition-colors">
                        <option value="">Selecteer...</option>
                        {['ABN AMRO','Aegon','Allianz','Argenta','ASN Bank','BLG Wonen','Florius','Handelsbanken','ING','Lloyds Bank','Munt Hypotheken','Nationale Nederlanden','Obvion','Rabobank','RegioBank','SNS','Van Lanschot','Anders'].map(b => <option key={b} value={b}>{b}</option>)}
                      </select>
                      {data.mortgageProvider === 'Anders' && (
                        <Input label="Naam verstrekker" value={data.mortgageProviderOther}
                          onChange={e => upd('mortgageProviderOther', e.target.value)}
                          placeholder="Naam hypotheekverstrekker" />
                      )}
                    </div>
                  )}
                  {/* Buitenland: vrij veld */}
                  {data.mortgageCountry && data.mortgageCountry !== 'NL' && (
                    <Input label="Naam hypotheekverstrekker" value={data.mortgageProvider}
                      onChange={e => upd('mortgageProvider', e.target.value)}
                      placeholder={`Bijv: ${{IT: 'Unicredit, Intesa Sanpaolo', ES: 'Santander, BBVA', FR: 'BNP Paribas, Crédit Agricole', DE: 'Deutsche Bank, Commerzbank', BE: 'BNP Paribas Fortis, ING België'}[data.mortgageCountry] ?? 'Naam van de bank'}` } />
                  )}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Input label="Hypotheeknummer / leningnummer" value={data.mortgageNumber}
                      onChange={e => upd('mortgageNumber', e.target.value)}
                      placeholder="Bijv: HYP-123456" />
                    <Input label="Contactgegevens bank (optioneel)" value={data.mortgageContact}
                      onChange={e => upd('mortgageContact', e.target.value)}
                      placeholder="Telefoon / e-mail" />
                  </div>
                  <Input label="Opmerkingen (optioneel)" value={data.mortgageNotes}
                    onChange={e => upd('mortgageNotes', e.target.value)}
                    placeholder="Bijv: loopt af 2032, eigendom samen met partner..." />
                </div>
              )}
              {/* Legacy fallback */}
              {data.mortgage && !data.hasMortgage && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <p className="text-xs text-amber-800">
                    <strong>Eerder ingevuld:</strong> {data.mortgage}
                  </p>
                </div>
              )}
            </div>
          </Card>

        </div>
      )}
    </div>
  )
}
